import dayjs from "dayjs";
import PropTypes from "prop-types";

import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
} from "../../shared";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
    marginTop: 16,
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#ececec",
    fontFamily: "Helvetica-Bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: "row",
    borderColor: "#ececec",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  cell: {
    padding: 4,
  },
  attributeHeader: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginTop: 8,
  },
  infoSection: {
    flexDirection: "row",
    marginVertical: 16,
  },
  infoItem: {
    flex: 1,
  },
  totalSection: {
    alignItems: "flex-end",
    marginTop: 16,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    minWidth: 200,
    marginBottom: 4,
  },
  grandTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    minWidth: 200,
    marginBottom: 4,
    fontFamily: "Helvetica-Bold",
    fontSize: 12,
    borderTopWidth: 1,
    borderTopColor: "#364F6B",
    paddingTop: 4,
  },
  productGroupHeader: {
    backgroundColor: "#f5f5f5",
    paddingHorizontal: 8,
    paddingVertical: 6,
    marginTop: 8,
  },
  productGroupTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
  },
});

export const PurchaseOrderLabPdf = ({ purchaseOrder, user }) => {
  const subtotal = purchaseOrder.purchaseOrderProductVariants.reduce(
    (sum, variant) => {
      const itemTotal =
        variant.quantity * variant.costPerUnit - (variant.discount || 0);
      return sum + itemTotal;
    },
    0
  );

  // Get product name and addOn from first variant
  const firstVariant = purchaseOrder.purchaseOrderProductVariants[0];
  const productName = firstVariant.product.name;

  // Get addOn from the first variant's note
  let addOn = "-";
  try {
    const note = firstVariant?.note || "{}";
    const parsed = JSON.parse(note);
    addOn = parsed.addOn || "-";
  } catch {
    addOn = "-";
  }

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <View>
            <Text style={styles.header}>Pesanan Pembelian Lab.</Text>
            <Text
              style={{
                fontSize: 12,
                color: "#364F6B",
                fontFamily: "Helvetica-Bold",
                marginTop: 4,
              }}
            >
              {purchaseOrder.referenceNumber}
            </Text>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                textAlign: "right",
              }}
            >
              Dicetak oleh: {user.name}
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                marginTop: 2,
                textAlign: "right",
              }}
            >
              Tanggal cetak: {formatDateTime(dayjs())}
            </Text>
          </View>
        </View>

        {/* Information Section */}
        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Pembeli</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.buyer.name}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Gudang</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.warehouse.name}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Penjual</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.seller.name}
            </Text>
          </View>
        </View>

        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Tanggal Transaksi</Text>
            <Text style={styles.attributeHeader}>
              {formatDate(purchaseOrder.transactionDate)}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Syarat Pembayaran</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.paymentTerm?.name ?? "-"}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Kena Pajak</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.taxable ? "Ya" : "Tidak"}
            </Text>
          </View>
        </View>

        <View style={styles.infoSection}>
          <View style={[styles.infoItem, { flex: 2 }]}>
            <Text>Catatan</Text>
            <Text style={styles.attributeHeader}>
              {purchaseOrder.note ?? "-"}
            </Text>
          </View>
        </View>

        {/* Lab Products Table */}
        <Text style={styles.sectionTitle}>Detail Produk Lab</Text>

        {/* Single Product Group Header */}
        <View style={styles.productGroupHeader}>
          <Text style={styles.productGroupTitle}>{productName}</Text>
          <Text style={{ fontSize: 9, marginTop: 2 }}>Add On: {addOn}</Text>
        </View>

        {/* Table Header */}
        <View style={styles.tableHeader}>
          <Text style={[styles.cell, { flex: 1 }]}>Sisi</Text>
          <Text style={[styles.cell, { flex: 4 }]}>SKU</Text>
          <Text style={[styles.cell, { flex: 2 }]}>Varian</Text>
          <Text style={[styles.cell, { flex: 1 }]}>PD/Prism</Text>
          <Text style={[styles.cell, { flex: 1.5, textAlign: "right" }]}>
            Harga (Rp)
          </Text>
        </View>

        {/* Table Rows - All variants in single table */}
        {purchaseOrder.purchaseOrderProductVariants.map((variant) => {
          // Get axis from note
          let axis = "-";
          try {
            const parsed = JSON.parse(variant.note || "{}");
            axis = parsed.axis || "-";
          } catch {
            // Ignore JSON parse errors
          }

          // Parse prescription parts: {sph}/{cyl}/{add?}/{side?}
          const prescriptionParts = variant.name
            ?.substring(variant.name.lastIndexOf(" ") + 1)
            .split("/");
          const sph = prescriptionParts?.[0] || "-";
          const cyl = prescriptionParts?.[1] || "-";
          const add = prescriptionParts?.[2] || "0"; // Use "0" if null
          // Ignore prescriptionParts[3] (side indicator)

          // Build prescription format: {sph}/{cyl}/{axis}/{add}
          const prescription = `${sph}/${cyl}/${axis}/${add}`;

          // Format PD/Prism
          let pd = "-",
            prism = "-";
          try {
            const parsed = JSON.parse(variant.note || "{}");
            pd = parsed.pd || "-";
            prism = parsed.prism || "-";
          } catch {
            // Ignore JSON parse errors
          }
          const pdPrism = `${pd}/${prism}`;

          return (
            <View style={styles.tableRow} key={variant.id}>
              <Text style={[styles.cell, { flex: 1 }]}>
                {variant.side || "-"}
              </Text>
              <Text style={[styles.cell, { flex: 4 }]}>{variant.sku}</Text>
              <Text style={[styles.cell, { flex: 2 }]}>{prescription}</Text>
              <Text style={[styles.cell, { flex: 1 }]}>{pdPrism}</Text>
              <Text style={[styles.cell, { flex: 1.5, textAlign: "right" }]}>
                {formatNumber(variant.costPerUnit || 0)}
              </Text>
            </View>
          );
        })}

        {/* Totals Section */}
        <View style={styles.totalSection}>
          <View style={styles.totalRow}>
            <Text>Subtotal:</Text>
            <Text>{formatMoney(purchaseOrder.subtotal || subtotal)}</Text>
          </View>
          {purchaseOrder.totalDiscount > 0 && (
            <View style={styles.totalRow}>
              <Text>Total Diskon:</Text>
              <Text>{formatMoney(purchaseOrder.totalDiscount)}</Text>
            </View>
          )}
          {purchaseOrder.taxable && (
            <View style={styles.totalRow}>
              <Text>Pajak ({purchaseOrder.taxPercentage || 0}%):</Text>
              <Text>{formatMoney(purchaseOrder.taxAmount || 0)}</Text>
            </View>
          )}
          <View style={styles.grandTotalRow}>
            <Text>Total:</Text>
            <Text>{formatMoney(purchaseOrder.grandTotal)}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

PurchaseOrderLabPdf.propTypes = {
  purchaseOrder: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};
